// Place search field: a WAI-ARIA 1.2 combobox over same-origin /api/geocode suggestions (never a third party).
// Below WIDE_MIN (layout.mjs) the pill opens a full-screen sheet; on desktop the field stays top left with a dropdown.
import {t, plural, locale} from './i18n.mjs';
import {icon} from './icons.mjs';

export const MIN_CHARS = 2, DEBOUNCE_MS = 200, TOUCH_DEBOUNCE_MS = 250;
export const KIND_ICONS = {address: 'house', street: 'route', poi: 'pin', parking: 'parking', station: 'train', city: 'city', district: 'city', region: 'city'};
// Addresses and named places speak for themselves; other kinds get a short word before the area.
const KIND_KEYS = {street: 'searchKindStreet', parking: 'searchKindParking', station: 'searchKindStation',
  city: 'searchKindCity', district: 'searchKindCity', region: 'searchKindCity'};
const CACHE = 50;

// Bias: the map center rounded to about 1 km, so the request does not carry a precise position.
export function suggestionURL(q, center, lang) {
  const p = new URLSearchParams({q, lang});
  if (center) { p.set('lat', center.lat.toFixed(2)); p.set('lon', center.lng.toFixed(2)); }
  return '/api/geocode?' + p;
}

// [text, bold] segments: typed words in bold, matched case-insensitively (rendered with textContent only).
export function marks(label, query) {
  const words = query.toLocaleLowerCase().split(/\s+/).filter(w => w.length >= MIN_CHARS);
  const lower = label.toLocaleLowerCase(), bold = new Array(label.length).fill(false);
  for (const word of words) for (let i = lower.indexOf(word); i >= 0; i = lower.indexOf(word, i + 1)) bold.fill(true, i, i + word.length);
  const out = [];
  for (let i = 0; i < label.length; i++) {
    if (out.length && out.at(-1)[1] === bold[i]) out.at(-1)[0] += label[i]; else out.push([label[i], bold[i]]);
  }
  return out;
}

// A picked place as a search transition (see app.mjs applySearch): address origin, town extent as bounds, no hits.
export function placeTransition(place, filters) {
  const [w, s, e, n] = place.extent || [];
  return {origin: {source: 'address', point: [place.lon, place.lat], label: place.label, autoGPS: false},
    filters: {...filters}, hits: [], bounds: place.extent ? [[w, s], [e, n]] : []};
}

export function placeSearch({onPick, onClear, center = () => null, wide = () => true, coarse = () => false,
  fetcher = (...a) => fetch(...a), timers = globalThis, doc = document} = {}) {
  const $ = id => doc.getElementById(id);
  const root = $('place-search'), input = $('search-input'), list = $('search-list'), status = $('search-status');
  const cache = new Map();
  let results = [], active = -1, committed = '', timer = null, controller = null, sequence = 0, pending = null, quiet = false;

  function setOpen(open) {
    const was = root.hasAttribute('data-open');
    root.toggleAttribute('data-open', open);
    doc.documentElement.toggleAttribute('data-search-open', open && !wide());
    if (!open) { list.hidden = true; input.setAttribute('aria-expanded', 'false'); setActive(-1); }
    if (was !== open) syncClear();
  }
  function syncClear() { $('search-clear').hidden = !input.value; }
  function say(text, visible = false) { status.textContent = text; status.classList.toggle('is-visible', visible && !!text); }
  function setActive(index) {
    active = index;
    for (const [i, option] of [...list.children].entries()) option.setAttribute('aria-selected', String(i === index));
    if (index >= 0) { input.setAttribute('aria-activedescendant', 'search-option-' + index); list.children[index]?.scrollIntoView?.({block: 'nearest'}); }
    else input.removeAttribute('aria-activedescendant');
  }
  function render(query) {
    list.replaceChildren(...results.map((place, i) => {
      const option = doc.createElement('li'), glyph = doc.createElement('span'), text = doc.createElement('span');
      const name = doc.createElement('span'), detail = doc.createElement('span');
      option.id = 'search-option-' + i; option.className = 'search-option'; option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false'); option.dataset.kind = place.kind;
      glyph.className = 'search-kind'; glyph.innerHTML = icon(KIND_ICONS[place.kind] || 'pin');
      name.className = 'search-name';
      for (const [part, bold] of marks(place.label, query)) {
        const node = bold ? doc.createElement('b') : doc.createTextNode(part);
        if (bold) node.textContent = part;
        name.append(node);
      }
      detail.className = 'search-detail'; detail.textContent = [KIND_KEYS[place.kind] && t(KIND_KEYS[place.kind]), place.detail].filter(Boolean).join(' · ');
      text.className = 'search-text'; text.append(name, detail);
      option.append(glyph, text);
      // pointerdown keeps focus in the field; click picks (mouse, touch and pen alike).
      option.addEventListener('pointerdown', event => event.preventDefault());
      option.addEventListener('click', () => pick(i));
      return option;
    }));
    const open = root.hasAttribute('data-open') && results.length > 0;
    list.hidden = !open; input.setAttribute('aria-expanded', String(open)); setActive(-1);
  }
  function show(found, query) {
    results = found; render(query);
    if (!found.length) say(t('searchNoResults'), true);
    else say(plural('searchResults', found.length));
  }
  async function load(query) {
    const key = query.toLocaleLowerCase() + '|' + locale();
    if (cache.has(key)) { show(cache.get(key), query); return results; }
    controller?.abort(); controller = new AbortController();
    const mine = ++sequence;
    try {
      const response = await fetcher(suggestionURL(query, center(), locale().slice(0, 2)), {signal: controller.signal});
      if (response.status === 429) return results;  // Keep the current list; the next keystroke retries.
      if (!response.ok) throw Error(String(response.status));
      const found = (await response.json()).results || [];
      if (mine !== sequence) return results;
      cache.set(key, found); if (cache.size > CACHE) cache.delete(cache.keys().next().value);
      show(found, query);
    } catch (error) {
      if (error?.name === 'AbortError' || mine !== sequence) return results;
      results = []; render(query); say(t('searchUnavailable'), true);
    }
    return results;
  }
  function schedule() {
    timers.clearTimeout(timer); syncClear();
    const query = input.value.trim();
    if (query.length < MIN_CHARS) { controller?.abort(); sequence++; results = []; render(query); say(''); pending = null; return; }
    pending = new Promise(resolve => { timer = timers.setTimeout(() => resolve(load(query)), coarse() ? TOUCH_DEBOUNCE_MS : DEBOUNCE_MS); });
  }
  function pick(index) {
    const place = results[index];
    if (!place) return;
    committed = place.label; input.value = place.label;
    close(); onPick?.(place);
    // Desktop keeps focus in the field (closing a panel would return it to the panel's opener); mobile drops the keyboard.
    if (wide()) { quiet = true; input.focus(); quiet = false; setOpen(false); }
  }
  function close(restore = false) {
    timers.clearTimeout(timer); controller?.abort(); sequence++;
    if (restore) input.value = committed;
    setOpen(false); say(''); input.blur();
  }
  function clear() {
    const had = !!committed;
    committed = ''; input.value = ''; results = []; render(''); say(''); syncClear();
    if (had) onClear?.();
    // In the open sheet or on desktop keep typing; the mobile pill clears without opening the sheet.
    if (wide() || root.hasAttribute('data-open')) input.focus();
  }
  async function submit() {
    const query = input.value.trim();
    if (active >= 0) { pick(active); return; }
    if (query.length < MIN_CHARS) return;
    timers.clearTimeout(timer);
    await load(query);
    if (input.value.trim() === query) pick(0);
  }

  input.addEventListener('focus', () => { if (quiet) return; setOpen(true); input.select?.(); if (input.value.trim().length >= MIN_CHARS && input.value !== committed) schedule(); });
  input.addEventListener('input', () => { setOpen(true); schedule(); });
  input.addEventListener('keydown', event => {
    const n = results.length;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!n) return;
      list.hidden = false; input.setAttribute('aria-expanded', 'true');
      setActive(event.key === 'ArrowDown' ? (active + 1) % n : (active <= 0 ? n - 1 : active - 1));
    } else if (event.key === 'Enter') { event.preventDefault(); submit(); }
    else if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation();
      if (!list.hidden && wide()) { list.hidden = true; input.setAttribute('aria-expanded', 'false'); setActive(-1); }
      else close(true);
    } else if (event.key === 'Tab' && wide()) setOpen(false);
  });
  root.addEventListener('focusout', event => { if (wide() && !root.contains(event.relatedTarget)) { setOpen(false); input.value ||= committed; syncClear(); } });
  $('search-clear').addEventListener('click', clear);
  $('search-back').addEventListener('click', () => close(true));
  // "/" focuses the field from anywhere outside other text inputs.
  doc.addEventListener('keydown', event => {
    if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || event.target?.closest?.('input, textarea, select, [contenteditable]')) return;
    event.preventDefault(); input.focus();
  });

  return {
    // A place applied from elsewhere (assistant link or search session): show its label, or nothing.
    show(label) { committed = label || ''; if (doc.activeElement !== input) input.value = committed; syncClear(); },
    get pending() { return pending; }, get results() { return results; },
  };
}
